import {
  Avatar,
  Box,
  Button,
  Circle,
  Flex,
  HStack,
  Icon,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Spinner,
  Text,
  useDisclosure,
  useToast,
  VStack
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiBell } from "react-icons/fi";
import { PiBellDuotone } from "react-icons/pi";
import {
  AppNotification,
  getNotifications,
  getUnreadCount,
  markAllNotificationsRead,
  markNotificationRead
} from "@lubbezposrednio-frontend/core";

// Uwaga: „Do zrobienia" (potwierdzenie głosu w apce / zatwierdzenie logowania 2FA) to drugi czynnik
// na zaufanym TELEFONIE — celowo NIE pokazujemy go na webie. Dzwonek webowy = tylko powiadomienia społecznościowe.

const relTime = (iso: string): string => {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "teraz";
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} godz.`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} dni`;
  return new Date(iso).toLocaleDateString("pl-PL");
};

const mediaUrl = (p?: string | null) => (p ? `${process.env.REACT_APP_BACKEND_URL}${p}` : undefined);

const targetPath = (n: AppNotification): string | null => {
  if (n.target === "voting" && n.target_id) return `/glosowanie/${n.target_id}`;
  // „Pro" deep-link: reakcja/komentarz/odpowiedź/wzmianka → DOKŁADNIE ten post (permalink), nie góra ściany.
  if (n.target === "post" && n.target_id && n.group_id) return `/organizator/grupy/${n.group_id}/post/${n.target_id}`;
  // Post portalowy (niegrupowy) → permalink w Społeczności.
  if (n.target === "portal" && n.target_id) return `/organizator/post-portalowy/${n.target_id}`;
  // Nowy obserwujący → jego profil (o ile publiczny; bez refa — brak linku, tylko treść).
  if (n.target === "profile") return n.target_id ? `/profil/${n.target_id}` : null;
  const gid = n.target_id || n.group_id;
  // Prośba o dołączenie → wprost do „Osoby → Oczekujący". Nowy członek → zakładka „Osoby".
  if (n.target === "group_pending" && gid) return `/organizator/grupy/${gid}?tab=people&filter=pending`;
  if (n.target === "group_members" && gid) return `/organizator/grupy/${gid}?tab=people`;
  if (n.target === "group" && gid) return `/organizator/grupy/${gid}`;
  return null;
};

const NotificationBell: React.FC<{ variant?: "icon" | "nav" }> = ({ variant = "icon" }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(false);

  const refreshCount = useCallback(() => {
    getUnreadCount()
      .then(r => setUnread(r.data.unread_count))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    refreshCount();
    const t = setInterval(refreshCount, 45000); // brak websocketów → lekki polling
    return () => clearInterval(t);
  }, [refreshCount]);

  const loadList = useCallback(() => {
    setLoading(true);
    getNotifications(1)
      .then(r => {
        setItems(r.data.notifications);
        setUnread(r.data.meta.unread_count);
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, []);

  const handleOpen = () => {
    onOpen();
    loadList();
  };

  const openItem = (n: AppNotification) => {
    if (!n.read) {
      markNotificationRead(n.id).catch(() => undefined);
      setItems(prev => prev.map(x => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread(u => Math.max(0, u - 1));
    }
    onClose();
    const path = targetPath(n);
    if (path) navigate(path);
  };

  const markAll = () => {
    markAllNotificationsRead().catch(() => undefined);
    setItems(prev => prev.map(x => ({ ...x, read: true })));
    setUnread(0);
  };

  return (
    <Popover isOpen={isOpen} onOpen={handleOpen} onClose={onClose} placement={variant === "nav" ? "right-start" : "bottom-end"} isLazy>
      <PopoverTrigger>
        {variant === "nav" ? (
          // Pełnowymiarowy wiersz nawigacji w sidebarze — czytelny: dzwonek + „Powiadomienia" + licznik.
          <HStack
            as="button"
            type="button"
            w="100%"
            spacing="13px"
            px="12px"
            py="9px"
            borderRadius="12px"
            cursor="pointer"
            transition="all 0.16s cubic-bezier(0.22, 1, 0.36, 1)"
            bg={isOpen ? "rgba(24,12,48,0.06)" : "transparent"}
            _hover={{ bg: isOpen ? "rgba(24,12,48,0.08)" : "rgba(24,12,48,0.035)" }}
          >
            <Box position="relative" display="inline-flex">
              <Icon as={PiBellDuotone} boxSize="21px" color="#f59e0b" />
              {unread > 0 && <Circle size="9px" bg="accent" position="absolute" top="-2px" right="-2px" border="2px solid white" />}
            </Box>
            <Text fontSize="14.5px" fontWeight="600" color="#4a4458">Powiadomienia</Text>
            {unread > 0 && (
              <Flex ml="auto" minW="20px" h="20px" px="6px" bg="accent" color="white" borderRadius="full" align="center" justify="center" fontSize="11px" fontWeight="800">
                {unread > 99 ? "99+" : unread}
              </Flex>
            )}
          </HStack>
        ) : (
          <Box position="relative" display="inline-flex">
            <IconButton aria-label="Powiadomienia" icon={<FiBell size={20} />} variant="ghost" color="primary" />
            {unread > 0 && (
              <Circle size="18px" bg="accent" color="white" fontSize="10px" fontWeight="800" position="absolute" top="2px" right="2px" border="2px solid white">
                {unread > 9 ? "9+" : unread}
              </Circle>
            )}
          </Box>
        )}
      </PopoverTrigger>
      <Portal>
      <PopoverContent className="notif-popover" w="360px" maxW="92vw" borderRadius="16px" boxShadow="0 16px 40px rgba(67,0,152,0.16)" overflow="hidden">
        <PopoverArrow />
        <PopoverHeader borderBottomWidth="1px" borderColor="rgba(67,0,152,0.08)" py="11px">
          <Flex justify="space-between" align="center">
            <Text fontWeight="800" color="primary" fontSize="15px">Powiadomienia</Text>
            {items.some(i => !i.read) && (
              <Button variant="link" size="xs" color="primary" fontWeight="700" onClick={markAll}>
                Oznacz wszystkie
              </Button>
            )}
          </Flex>
        </PopoverHeader>
        <PopoverBody p={0} maxH="420px" overflowY="auto">
          {loading ? (
            <Flex justify="center" py="36px"><Spinner color="primary" /></Flex>
          ) : items.length === 0 ? (
            <VStack py="40px" spacing="8px" color="gray.400">
              <Icon as={FiBell} boxSize="24px" />
              <Text fontSize="14px">Brak powiadomień</Text>
            </VStack>
          ) : (
            items.map(n => (
              <HStack
                key={n.id}
                align="flex-start"
                spacing="11px"
                px="14px"
                py="11px"
                cursor="pointer"
                bg={n.read ? "white" : "rgba(67,0,152,0.04)"}
                _hover={{ bg: "rgba(67,0,152,0.07)" }}
                borderBottom="1px solid rgba(67,0,152,0.05)"
                onClick={() => openItem(n)}
              >
                <Avatar size="sm" name={n.actor?.name || "?"} src={mediaUrl(n.actor?.avatar_url)} bg="rgba(67,0,152,0.1)" color="primary" />
                <Box flex="1" minW={0}>
                  <Text fontSize="13.5px" color="gray.700" noOfLines={2}>{n.message}</Text>
                  <Text fontSize="11.5px" color="gray.400" mt="2px">{relTime(n.updated_at)}</Text>
                </Box>
                {!n.read && <Circle size="8px" bg="accent" mt="6px" flexShrink={0} />}
              </HStack>
            ))
          )}
        </PopoverBody>
      </PopoverContent>
      </Portal>
    </Popover>
  );
};

export default NotificationBell;
